/**
 * Cover-fit scale calculation for masked uploads.
 *
 * The editor contain-fits the uploaded image to the full standardised canvas
 * (1250px wide, height from the step image aspect) and then multiplies by the
 * user `scale`. The mask only occupies `positioning.scale`% of the canvas
 * width, so scale 1 leaves the image far larger than the mask. This computes
 * the scale at which the image exactly covers the mask (like object-fit:
 * cover — sized to mask width or height, whichever fit is larger).
 */

const STANDARD_CANVAS_WIDTH = 1250;

const isPositiveFinite = (value: number): boolean => Number.isFinite(value) && value > 0;

export interface MaskCoverFitScaleOptions {
  /** Natural width of the uploaded image */
  imageWidth: number;
  /** Natural height of the uploaded image */
  imageHeight: number;
  /** Standardised step image dimensions (1250-wide system) */
  stepImageDimensions: { width: number; height: number };
  /** Mask width as a percentage of canvas width (positioning.scale, e.g. 40) */
  maskScalePercent: number;
  /** Mask image dimensions — only the aspect ratio is used (maskImageConfig) */
  maskAspect: { width: number; height: number };
  /** Clamp floor; pass 0 to disable. Defaults to the editor's zoom minimum. */
  minScale?: number;
  /** Clamp ceiling; pass Infinity to disable. Defaults to the editor's zoom maximum. */
  maxScale?: number;
}

export const calculateMaskCoverFitScale = (options: MaskCoverFitScaleOptions): number => {
  const { imageWidth, imageHeight, stepImageDimensions, maskScalePercent, maskAspect, minScale = 0.1, maxScale = 3 } = options;

  if (
    !isPositiveFinite(imageWidth) ||
    !isPositiveFinite(imageHeight) ||
    !stepImageDimensions ||
    !isPositiveFinite(stepImageDimensions.width) ||
    !isPositiveFinite(stepImageDimensions.height) ||
    !isPositiveFinite(maskScalePercent) ||
    !maskAspect ||
    !isPositiveFinite(maskAspect.width) ||
    !isPositiveFinite(maskAspect.height)
  ) {
    return 1;
  }

  const canvasWidth = STANDARD_CANVAS_WIDTH;
  const canvasHeight = (canvasWidth * stepImageDimensions.height) / stepImageDimensions.width;

  // How the image is contain-fit to the canvas before user scale applies
  const imageAspectRatio = imageWidth / imageHeight;
  const canvasAspectRatio = canvasWidth / canvasHeight;

  let baseScaledWidth: number;
  let baseScaledHeight: number;

  if (imageAspectRatio > canvasAspectRatio) {
    // Image is wider - constrain by width to fit within canvas
    baseScaledWidth = canvasWidth;
    baseScaledHeight = canvasWidth / imageAspectRatio;
  } else {
    // Image is taller - constrain by height to fit within canvas
    baseScaledHeight = canvasHeight;
    baseScaledWidth = canvasHeight * imageAspectRatio;
  }

  // Mask dimensions in canvas coordinates (see applyPNGMaskClipping)
  const maskWidth = canvasWidth * (maskScalePercent / 100);
  const maskHeight = maskWidth * (maskAspect.height / maskAspect.width);

  // The final rendered size is baseScaled * scale; both dimensions must be
  // >= the mask dimensions to cover it, so take the larger ratio.
  const scaleX = maskWidth / baseScaledWidth;
  const scaleY = maskHeight / baseScaledHeight;
  const scaleToFitMask = Math.max(scaleX, scaleY);

  return Math.min(maxScale, Math.max(minScale, scaleToFitMask));
};
